import React, { useState, useEffect, useCallback } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, IconButton, Toolbar } from '@mui/material';
import { useLanguage } from 'Context/LanguageContext';
import { useSideBar, useSideBarUpdate } from 'Context/SideBarContext';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { removeSession } from 'FielderUtils/session/Session';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isLocalUser } from 'Utils/account/utils';
import { ENDPOINTS } from '@core/constants/pageEndpoints.constants';
import { selectAccount, selectUser } from '../../core/store/modules/session/sessionSelectors';
import { setUserInfo, setLogOutUser } from '../../core/store/modules/session/sessionSlice';
import AccountSelect from './components/AccountSelect';
import NonPaymentWarning from './components/NonPaymentWarning';
import SearchBar from './components/SearchBar';
import TopMenuAddUser from './components/TopMenuAddUser';
import TopMenuCreateTask from './components/TopMenuCreateTask';
import TopMenuNotification from './components/TopMenuNotification';
import TopMenuProfile from './components/TopMenuProfile/TopMenuProfile';
import TopMenuShare from './components/TopMenuShare';
import { getTopMenuInfo } from '@core/api/user/user.service';
import { useTheme } from '@mui/material/styles';
import FielderLogoNegative from '@assets/icons/logo-login-icon-light.svg';
import FielderLogoImage from '@assets/icons/logo-login-icon.svg';

const LoggedMenu = () => {
  const user = useSelector(selectUser);
  const account = useSelector(selectAccount);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lan = useLanguage();
  const sideBarInfo = useSideBar();
  const setSideBar = useSideBarUpdate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const logoSrc = isDarkMode ? FielderLogoNegative : FielderLogoImage;

  const [logo, setLogo] = useState(logoSrc);
  const [nonPaymentCounter, setNonPaymentCounter] = useState(-1);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isInternalAdmin, setIsInternalAdmin] = useState(false);

  useEffect(() => {
    if (isLocalUser(user)) {
      const onSuccess = ({ user: nextUser, notificationsCount }) => {
        setNotificationCount(notificationsCount);
        const nextStatus = nextUser.status;

        if (nextStatus == null || nextStatus.status === 1) {
          dispatch(setLogOutUser());
          removeSession();
          navigate(ENDPOINTS.login);
        } else {
          const profileImage = nextUser?.profileImage?.scalePath;
          const { users } = nextUser;
          dispatch(
            setUserInfo({
              status: nextStatus,
              profileImage,
              users,
            })
          );
        }
      };

      const onError = (e) => {
        console.error('[FIELDER - ERROR - LOGGED MENU]', e);
      };

      getTopMenuInfo(onSuccess, onError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsInternalAdmin(RoleUtils.isInternalAdminUser(user));
  }, [user]);

  const setAccountInfo = useCallback((accountData) => {
    if (accountData) {
      if (accountData.logo) setLogo(accountData.logo);
      if (accountData.nonPayment > 0) setNonPaymentCounter(accountData.nonPayment);
    }
  }, []);

  useEffect(() => {
    if (account) {
      setAccountInfo(account);
    }
  }, [account, setAccountInfo]);

  const openDrawer = () => {
    setSideBar(true);
  };

  const updateNotificationCount = (number) => {
    setNotificationCount(number);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ gap: 1, px: 2 }}>
          {/* Drawer toggle button (hidden when sidebar is open) */}
          <IconButton
            edge="start"
            onClick={openDrawer}
            aria-label={lan.openMenu ?? 'Open menu'}
            sx={{
              mr: 1.5,
              ...(sideBarInfo?.open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Brand logo (default: logo-login-icon-light.svg or account logo) */}
          <Box
            component="img"
            src={logoSrc}
            alt="brand"
            sx={{
              height: 28,
              width: 'auto',
              display: 'block',
            }}
          />

          {/* Main layout grid */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              columnGap: 2,
              ml: 2,
              minWidth: 0,
            }}
          >
            {isInternalAdmin ? (
              // Internal admin: only profile on the right side
              <Box sx={{ justifySelf: 'end' }}>
                <TopMenuProfile lan={lan} user={user} />
              </Box>
            ) : (
              <>
                {/* LEFT: Search */}
                <Box sx={{ justifySelf: 'start', minWidth: 0 }}>
                  <SearchBar />
                </Box>

                {/* CENTER: main actions */}
                <Box
                  sx={{
                    justifySelf: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    '& > *': { display: 'flex', alignItems: 'center' },
                  }}
                >
                  <Box sx={{ pt: 0 }}>
                    <TopMenuCreateTask />
                  </Box>
                  <Box sx={{ pt: 0 }}>
                    <TopMenuAddUser user={user} />
                  </Box>
                  <Box sx={{ pt: 0 }}>
                    <TopMenuShare user={user} lan={lan} />
                  </Box>
                  <Box sx={{ pt: 0 }}>
                    <AccountSelect lan={lan} user={user} />
                  </Box>
                </Box>

                {/* RIGHT: Notifications + Profile */}
                <Box
                  sx={{
                    justifySelf: 'end',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0,
                    height: 48,
                  }}
                >
                  <TopMenuNotification
                    user={user}
                    onUpdate={updateNotificationCount}
                    notificationCount={notificationCount}
                  />
                  <TopMenuProfile />
                </Box>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {nonPaymentCounter > 0 && (
        <NonPaymentWarning user={user} nonPaymentCounter={nonPaymentCounter} />
      )}
    </>
  );
};
export default LoggedMenu;
//Original code:
/*import React, { useState, useEffect, useCallback } from 'react';
import Logo from '@assets/icons/logo-login-icon.svg';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Toolbar } from '@mui/material';
import { useLanguage } from 'Context/LanguageContext';
import { useSideBar, useSideBarUpdate } from 'Context/SideBarContext';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { removeSession } from 'FielderUtils/session/Session';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isLocalUser } from 'Utils/account/utils';
import { ENDPOINTS } from '@core/constants/pageEndpoints.constants';
import { selectAccount, selectUser } from '../../core/store/modules/session/sessionSelectors';
import { setUserInfo, setLogOutUser } from '../../core/store/modules/session/sessionSlice';
import AccountSelect from './components/AccountSelect';
import NonPaymentWarning from './components/NonPaymentWarning';
import SearchBar from './components/SearchBar';
import TopMenuAddUser from './components/TopMenuAddUser';
import TopMenuCreateTask from './components/TopMenuCreateTask';
import TopMenuNotification from './components/TopMenuNotification';
import TopMenuProfile from './components/TopMenuProfile/TopMenuProfile';
import TopMenuShare from './components/TopMenuShare';
import { AppBar, MenuButton } from './styled';
import { getTopMenuInfo } from '@core/api/user/user.service';

const LoggedMenu = () => {
	const user = useSelector(selectUser);
	const account = useSelector(selectAccount);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const lan = useLanguage();
	const sideBarInfo = useSideBar();
	const setSideBar = useSideBarUpdate();
	const [logo, setLogo] = useState(Logo);
	const [nonPaymentCounter, setNonPaymentCounter] = useState(-1);
	const [notificationCount, setNotificationCount] = useState(0);
	const [isInternalAdmin, setIsInternalAdmin] = useState(false);
	useEffect(() => {
		if (isLocalUser(user)) {
			const onSuccess = ({ user, notificationsCount }) => {
				setNotificationCount(notificationsCount);
				const nextStatus = user.status;
				if (nextStatus == null || nextStatus.status === 1) {
					dispatch(setLogOutUser());
					removeSession();
					navigate(ENDPOINTS.login);
				} else {
					const profileImage = user?.profileImage?.scalePath;
					const { users } = user;
					dispatch(
						setUserInfo({ status: nextStatus, profileImage, users, })
					);
				}
			};
			const onError = (e) => {
				console.error('[FIELDER - ERROR - LOGGED MENU]', e);
			};
			getTopMenuInfo(onSuccess, onError)
		}
	}, []);
	useEffect(() => {
		setIsInternalAdmin(RoleUtils.isInternalAdminUser(user));
	}, [user]);
	const setAccountInfo = useCallback((account) => {
		if (account) {
			if (account.logo) setLogo(account.logo);
			if (account.nonPayment > 0) setNonPaymentCounter(account.nonPayment);
		}
	}, []);
	useEffect(() => {
		setAccountInfo(account);
	}, [setAccountInfo]);
	useEffect(() => {
		if (account && account.logo) setLogo(account.logo);
	}, [account]);
	const openDrawer = () => {
		setSideBar(true);
	};
	const updateNotificationCount = (number) => {
		setNotificationCount(number);
	};
	return (
		<>
			<AppBar
        open={sideBarInfo.open}
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 1, px: 2 }}>
          <MenuButton onClick={openDrawer} edge='start' open={sideBarInfo.open}>
            <MenuIcon color='disabled' />
          </MenuButton>
          <Box component="img" src={logo} alt="brand" sx={{ height: 28, width: 'auto', display: 'block' }} />
          <Box
            sx={{
              flexGrow: 1,
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              columnGap: 2,
              ml: 2,
              minWidth: 0,
            }}
          >
            {isInternalAdmin ? (
              <Box sx={{ justifySelf: 'end' }}>
                <TopMenuProfile lan={lan} user={user} />
              </Box>
            ) : (
              <>
<Box sx={{ justifySelf: 'start', minWidth: 0 }}>
  <SearchBar />
</Box>
<Box
  sx={{
    justifySelf: 'center',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    '& > *': { display: 'flex', alignItems: 'center' },
  }}
>
  <Box sx={{ pt: 0 }}><TopMenuCreateTask /></Box>
  <Box sx={{ pt: 0 }}><TopMenuAddUser user={user} /></Box>
  <Box sx={{ pt: 0 }}><TopMenuShare user={user} lan={lan} /></Box>
  <Box sx={{ pt: 0 }}><AccountSelect lan={lan} user={user} /></Box>
</Box>
<Box
  sx={{
    justifySelf: 'end',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0,
    height: 48,
  }}
>
  <TopMenuNotification user={user} onUpdate={updateNotificationCount} />
  <TopMenuProfile />
</Box>
</>
)}
</Box>
</Toolbar>
</AppBar>
{nonPaymentCounter > 0 && <NonPaymentWarning user={user} nonPaymentCounter={nonPaymentCounter} />}
</>
);
};
export default LoggedMenu;
*/
